import type { Filter, FilterType, Group, Period, Rule, Template } from './types'

/** The filters a rule may use, in the order the picker shows them. */
export const FILTER_ORDER: FilterType[] = [
  'subscribed',
  'subscribed_within',
  'active_paid_subscription',
  'account_spend',
  'agency_spend',
  'active_chat',
  'in_list',
  'tracking_link',
  'blocked',
  'restricted',
  'deleted',
  'buying_power',
  'ppv_sent',
]

export const BOOLEAN_FILTERS: FilterType[] = ['subscribed', 'active_paid_subscription', 'blocked', 'restricted', 'deleted']
export const PERIOD_PRESETS = [7, 30, 90, 365]

export function defaultFilter(type: FilterType): Filter {
  switch (type) {
    case 'account_spend':
    case 'agency_spend':
      return { type, more_than: 0, up_to: null, period: { type: 'all_time' } }
    case 'active_chat':
      return { type, period: { type: 'last_days', days: 7 } }
    case 'subscribed_within':
      return { type, days: 7 }
    case 'in_list':
      return { type, list_ids: [] }
    case 'tracking_link':
      return { type, link_ids: [] }
    case 'buying_power':
      return { type, min_stars: 3, max_stars: 5 }
    case 'ppv_sent':
      return { type, at_least: 1, at_most: null }
    default:
      return { type, value: true }
  }
}

export function newRule(): Rule {
  return { version: 1, include: [{ filters: [defaultFilter('subscribed')] }], exclude: [] }
}

export type Side = 'include' | 'exclude'

/** Immutable edits, so the editor state stays a plain Rule. */
export const edit = {
  setFilter(rule: Rule, side: Side, g: number, f: number, filter: Filter): Rule {
    return mapGroup(rule, side, g, (group) => ({ filters: group.filters.map((x, i) => (i === f ? filter : x)) }))
  },
  addFilter(rule: Rule, side: Side, g: number, type: FilterType): Rule {
    return mapGroup(rule, side, g, (group) => ({ filters: [...group.filters, defaultFilter(type)] }))
  },
  /** Removing a group's last filter removes the group. */
  removeFilter(rule: Rule, side: Side, g: number, f: number): Rule {
    const groups = rule[side]
      .map((group, i) => (i === g ? { filters: group.filters.filter((_, j) => j !== f) } : group))
      .filter((group) => group.filters.length > 0)
    return { ...rule, [side]: groups }
  },
  addGroup(rule: Rule, side: Side, type: FilterType): Rule {
    return { ...rule, [side]: [...rule[side], { filters: [defaultFilter(type)] }] }
  },
}

/**
 * Whether the rule fits the one-row-per-filter editor: at most one include group (every
 * filter must match), and exclude groups of one filter each (any one leaves the fan out),
 * with no filter type twice on a side.
 */
export function isFlat(rule: Rule): boolean {
  if (rule.include.length > 1) return false
  if (rule.exclude.some((g) => g.filters.length !== 1)) return false
  const unique = (types: FilterType[]) => new Set(types).size === types.length
  return unique(rule.include[0]?.filters.map((f) => f.type) ?? []) && unique(rule.exclude.map((g) => g.filters[0]!.type))
}

/** The filter of this type on a side of a flat rule. */
export function flatFilter(rule: Rule, side: Side, type: FilterType): Filter | undefined {
  return side === 'include' ? rule.include[0]?.filters.find((f) => f.type === type) : rule.exclude.find((g) => g.filters[0]?.type === type)?.filters[0]
}

/** Immutable edits of a flat rule, one filter per type and side. */
export const flatEdit = {
  set(rule: Rule, side: Side, filter: Filter): Rule {
    if (side === 'exclude') {
      const at = rule.exclude.findIndex((g) => g.filters[0]?.type === filter.type)
      const exclude = at < 0 ? [...rule.exclude, { filters: [filter] }] : rule.exclude.map((g, i) => (i === at ? { filters: [filter] } : g))
      return { ...rule, exclude }
    }
    const filters = rule.include[0]?.filters ?? []
    const next = filters.some((f) => f.type === filter.type) ? filters.map((f) => (f.type === filter.type ? filter : f)) : [...filters, filter]
    return { ...rule, include: [{ filters: next }] }
  },
  remove(rule: Rule, side: Side, type: FilterType): Rule {
    if (side === 'exclude') return { ...rule, exclude: rule.exclude.filter((g) => g.filters[0]?.type !== type) }
    const filters = (rule.include[0]?.filters ?? []).filter((f) => f.type !== type)
    return { ...rule, include: filters.length ? [{ filters }] : [] }
  },
}

function mapGroup(rule: Rule, side: Side, g: number, fn: (group: Group) => Group): Rule {
  return { ...rule, [side]: rule[side].map((group, i) => (i === g ? fn(group) : group)) }
}

/** Why the rule cannot be saved yet (a translation key + values), or null. Server validation still applies. */
export function ruleProblem(rule: Rule, available: Set<FilterType>): { key: string; values?: Record<string, unknown> } | null {
  if (rule.include.length === 0) return { key: 'problem.noInclude' }
  for (const group of [...rule.include, ...rule.exclude]) {
    for (const f of group.filters) {
      if (!available.has(f.type)) return { key: 'problem.unavailable', values: { filter: f.type } }
      if ((f.type === 'account_spend' || f.type === 'agency_spend') && f.more_than == null && f.up_to == null) {
        return { key: 'problem.spendBound' }
      }
      if ((f.type === 'account_spend' || f.type === 'agency_spend') && f.more_than != null && f.up_to != null && f.up_to <= f.more_than) {
        return { key: 'problem.spendOrder' }
      }
      if (f.type === 'subscribed_within' && (!Number.isInteger(f.days) || f.days < 1 || f.days > 3650)) return { key: 'problem.days' }
      if (f.type === 'in_list' && f.list_ids.length === 0) return { key: 'problem.pickList' }
      if (f.type === 'tracking_link') {
        if (linkKeysOf(f).length === 0) return { key: 'problem.pickTrackingLink' }
        const d = f.first_seen_days
        if (d != null && (!Number.isInteger(d) || d < 1 || d > 3650)) return { key: 'problem.days' }
      }
      if ((f.type === 'account_spend' || f.type === 'agency_spend' || f.type === 'active_chat') && f.period.type === 'last_days') {
        if (!Number.isInteger(f.period.days) || f.period.days < 1 || f.period.days > 3650) return { key: 'problem.days' }
      }
    }
  }
  return null
}

type T = (key: string, values?: Record<string, unknown>) => string

const money = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`

export function describePeriod(p: Period, t: T): string {
  return p.type === 'all_time' ? t('period.allTime') : t('period.lastDays', { count: p.days })
}

/** A template's name and description in the UI language (the server's English when untranslated), and its icon. */
export function templateText(tpl: Template, t: T): { icon: string; name: string; description: string } {
  const text = (field: string, fallback: string) => {
    const key = `template.${tpl.key}.${field}`
    const v = t(key)
    return v === key ? fallback : v
  }
  return { icon: text('icon', ''), name: text('name', tpl.name), description: text('description', tpl.description) }
}

/** One filter as a short phrase, e.g. "Account spend over $50, up to $100 · all time". */
export function describeFilter(f: Filter, t: T, listName: (id: string) => string, linkName: (id: string) => string = (id) => `#${id}`): string {
  const label = t(`filter.${f.type}`)
  switch (f.type) {
    case 'account_spend':
    case 'agency_spend': {
      const parts = []
      if (f.more_than != null) parts.push(t('describe.over', { amount: money(f.more_than) }))
      if (f.up_to != null) parts.push(t('describe.upTo', { amount: money(f.up_to) }))
      return `${label} ${parts.join(', ')} · ${describePeriod(f.period, t)}`
    }
    case 'active_chat':
      return `${label} · ${describePeriod(f.period, t)}`
    case 'subscribed_within':
      return t('describe.subscribedWithin', { count: f.days })
    case 'in_list':
      return `${label}: ${f.list_ids.map(listName).join(t('describe.orJoin'))}`
    case 'tracking_link': {
      const name = (k: string) => (k.startsWith('trial:') ? t('param.trialLinkName', { name: linkName(k) }) : linkName(k))
      const links = `${label}: ${linkKeysOf(f).map(name).join(t('describe.orJoin'))}`
      return f.first_seen_days ? `${links} · ${t('describe.firstSeen', { count: f.first_seen_days })}` : links
    }
    case 'buying_power':
      return `${label} ${f.min_stars}–${f.max_stars}★`
    case 'ppv_sent': {
      const parts = []
      if (f.at_least != null) parts.push(t('describe.atLeast', { count: f.at_least }))
      if (f.at_most != null) parts.push(t('describe.atMost', { count: f.at_most }))
      return `${label} ${parts.join(', ')}`
    }
    default:
      return f.value ? label : t('describe.not', { label })
  }
}

/** The whole rule as lines: each include group, then "except …" for each exclude group. */
export function describeRule(
  rule: Rule,
  t: T,
  listName: (id: string) => string,
  linkName?: (id: string) => string,
): { include: string[]; exclude: string[] } {
  const group = (g: Group) => g.filters.map((f) => describeFilter(f, t, listName, linkName)).join(' · ')
  return { include: rule.include.map(group), exclude: rule.exclude.map(group) }
}

type LinkFilter = Extract<Filter, { type: 'tracking_link' }>

/**
 * The Link filter's links as keys: a tracking link's id, or "trial:<id>" for a
 * trial link (the two kinds number their ids separately; ADR 0039).
 */
export const linkKeysOf = (f: LinkFilter): string[] => [...f.link_ids, ...(f.trial_ids ?? []).map((id) => `trial:${id}`)]

/** The filter with its links set from keys (see linkKeysOf). */
export function withLinkKeys(f: LinkFilter, keys: string[]): LinkFilter {
  const trials = keys.filter((k) => k.startsWith('trial:')).map((k) => k.slice(6))
  const next: LinkFilter = { ...f, link_ids: keys.filter((k) => !k.startsWith('trial:')) }
  if (trials.length) next.trial_ids = trials
  else delete next.trial_ids
  return next
}
